import { DataField } from "@iventis/domain-model/model/dataField";
import React, { PropsWithChildren, useMemo } from "react";
import { Body2 } from "@iventis/styles/src/components/texts";
import { EventBlockTypeDataFields } from "@iventis/domain-model/model/eventBlockTypeDataFields";
import { DataFieldValue } from "@iventis/domain-model/model/dataFieldValue";
import { IventisFilterOperator } from "@iventis/domain-model/model/iventisFilterOperator";
import { resetChildDataFieldValuesInHierarchy } from "@iventis/utilities";
import { DataFieldInput } from "./formik-components/datafield-input";
import { getEventBlockDisplayValuesFromDataFieldValues } from "./event-block-helpers";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function EventDataFieldInputs<Values extends { dataFieldValues: { [key: string]: any } }>({
    eventBlockTypeDataFields,
    allDataFields,
    values,
    additionalNonEventBlockDataFieldValues,
    setFieldValue,
}: PropsWithChildren<{
    eventBlockTypeDataFields: EventBlockTypeDataFields[];
    allDataFields: DataField[];
    values: Values;
    /** Data field values that are not on the event block but may be parents of an event blocks' data field (e.g. discipline data field) */
    additionalNonEventBlockDataFieldValues: DataFieldValue[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}>) {
    /** Data field values in the domain format */
    const dataFieldValues = useMemo(
        () => Object.entries<DataFieldValue[]>(values.dataFieldValues).map(([key, value]) => ({ dataField: allDataFields.find((df) => df.id === key), value })),
        [values.dataFieldValues]
    );
    const allDataFieldValues: Values["dataFieldValues"] = useMemo(() => {
        const convertedAdditions = additionalNonEventBlockDataFieldValues.reduce((cum, dfv) => ({ ...cum, [dfv.dataField.id]: dfv.value }), {});
        return { ...values.dataFieldValues, ...convertedAdditions };
    }, [values, additionalNonEventBlockDataFieldValues]);

    /** Set the data field values in the parent form */
    const setDataFieldValues = (dataFieldId: string, value) => {
        const newValues = { ...values.dataFieldValues, [dataFieldId]: value };
        resetChildDataFieldValuesInHierarchy(dataFieldId, newValues, allDataFields);
        setFieldValue("dataFieldValues", newValues);
    };

    return (
        <>
            {eventBlockTypeDataFields
                .sort((a, b) => a.index - b.index)
                .map((eventBlockTypeDataField) => {
                    const filter = [];
                    let disabled = false;
                    const dataField = allDataFields.find((df) => df.id === eventBlockTypeDataField.dataFieldId);
                    const parentDataField = allDataFields.find((df) => df.id === dataField.parentDataFieldId);
                    if (parentDataField) {
                        const filterValue = allDataFieldValues[parentDataField.id]?.metaData?.[parentDataField.customerIdentifierName];
                        if (filterValue) {
                            filter.push({ fieldName: dataField.hierarchyMetadataPropertyName, operator: IventisFilterOperator.Eq, value: filterValue });
                        } else {
                            disabled = true;
                        }
                    }
                    return (
                        <DataFieldInput
                            dataField={dataField}
                            filter={filter}
                            autocompleteWithLoadingClassName="auto-complete"
                            displayCode
                            setFieldValue={setDataFieldValues}
                            handleChange={null}
                            values={values.dataFieldValues}
                            disabled={disabled}
                            key={dataField.id}
                        />
                    );
                })}
            <Body2>RSC: {getEventBlockDisplayValuesFromDataFieldValues(dataFieldValues, eventBlockTypeDataFields)?.code}</Body2>
        </>
    );
}
